/**
 * Admin API service – all requests to the local backend.
 * Base URL: relative (empty – proxied by nginx in production, Vite proxy in dev)
 * Uses Axios apiClient; global interceptor shows Arabic toast on failure.
 * Security Checklist: all mutation bodies are validated with shared Zod schemas before send.
 */

import { apiClient } from '@services/apiClient';
import type { AxiosError } from 'axios';
import {
  createAdminAccountBodySchema,
  updateAdminAccountBodySchema,
  updateAdminSettingsBodySchema,
  brokerInternalNoteBodySchema,
  createBrokerBodySchema,
  createDealBodySchema,
  addDealBrokerBodySchema,
  updateDealBrokerBodySchema,
  requestRefundBodySchema,
  payerRefundConfirmBodySchema,
  cancelDealBodySchema,
  rejectRefundBodySchema,
  brokerApproveBodySchema,
  refundApproveBodySchema,
  brokerRejectBodySchema,
  ibanBodySchema,
  profileFalBodySchema,
  platformResetBodySchema,
  type CreateDealBody,
  type PlatformResetBody,
} from '@schemas/formSchemas';
import type {
  Broker,
  BrokerInternalNote,
  Deal,
  DealActivityEvent,
  DealClientViewResponse,
} from '@saee/shared';

export type ApiError = {
  message: string;
  status?: number;
  code?: string;
  /** Field errors from API (`error.details`), when provided (e.g. auth/complete). */
  details?: Record<string, string[] | string>;
};

export type { Broker, BrokerInternalNote, Deal };
export type { DealActivityEvent };

/**
 * SaeeJSONRenderer wraps JSON as `{ success?, data: inner, meta? }`.
 * Views that return `Response({ "data": payload })` produce `inner === { data: payload }` — unwrap one level.
 * Flat objects and bare arrays appear as `inner` directly.
 */
function unwrapSaeeDataPayload<T>(res: { data?: unknown }): T | undefined {
  const body = res.data as { data?: unknown; meta?: unknown } | undefined;
  const inner = body?.data;
  if (inner === null || inner === undefined) return undefined;
  if (typeof inner === 'object' && inner !== null && !Array.isArray(inner) && 'data' in inner) {
    return (inner as { data: T }).data;
  }
  return inner as T;
}

function mapInternalNoteFromApi(raw: unknown): BrokerInternalNote {
  if (raw == null || typeof raw !== 'object') {
    return { id: '', body: '', authorName: '—', authorId: null, createdAt: null };
  }
  const r = raw as Record<string, unknown>;
  return {
    id: r.id != null ? String(r.id) : '',
    body: typeof r.body === 'string' ? r.body : '',
    authorName:
      typeof r.authorName === 'string'
        ? r.authorName
        : typeof r.author_name === 'string'
          ? r.author_name
          : '—',
    authorId:
      r.authorId != null ? String(r.authorId) : r.author_id != null ? String(r.author_id) : null,
    createdAt:
      (typeof r.createdAt === 'string' ? r.createdAt : null) ??
      (typeof r.created_at === 'string' ? r.created_at : null) ??
      null,
  };
}

/** Raw broker as returned by the backend (field names may differ) */
export interface BrokerRaw {
  id?: string;
  _id?: string;
  name?: string;
  mobile?: string;
  email?: string;
  nationalId?: string | null;
  dateOfBirth?: string | null;
  val_license?: string | null;
  falLicenseNumber?: string | null;
  val_verified?: boolean;
  falVerified?: boolean;
  iban?: string | null;
  status?: 'Active' | 'Pending Verification' | 'Pending_Nafath' | 'Suspended' | string;
  totalCommission?: number | null;
  createdAt?: string;
  updatedAt?: string;
  profileComplete?: boolean;
  profile_complete?: boolean;
  nationalAddress?: { id?: string; fullAddress?: string; full_address?: string } | null;
  national_address?: { id?: string; fullAddress?: string; full_address?: string } | null;
  [key: string]: unknown;
}

/**
 * Map backend broker payload to app Broker shape.
 * Backend may use val_license/val_verified or falLicenseNumber/falVerified; we normalize to a single interface.
 */
export function mapBrokerFromApi(raw: BrokerRaw): Broker {
  const id = raw.id ?? raw._id ?? '';
  return {
    id: String(id),
    name: raw.name ?? '',
    mobile: raw.mobile ?? '',
    email: raw.email ?? undefined,
    nationalId: raw.nationalId ?? (raw as { national_id?: string }).national_id ?? null,
    dateOfBirth: raw.dateOfBirth ?? (raw as { date_of_birth?: string }).date_of_birth ?? null,
    falLicenseNumber: raw.val_license ?? raw.falLicenseNumber ?? null,
    falVerified: raw.val_verified ?? raw.falVerified ?? false,
    iban: raw.iban ?? null,
    totalCommission: raw.totalCommission ?? null,
    status: raw.status ?? undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    profileComplete: raw.profileComplete ?? raw.profile_complete,
    internalNotes: (() => {
      const arr = raw.internalNotes ?? raw.internal_notes;
      return Array.isArray(arr) ? arr.map(mapInternalNoteFromApi) : undefined;
    })(),
    nationalAddress: (() => {
      const addr = raw.nationalAddress ?? raw.national_address;
      if (!addr || typeof addr !== 'object') return null;
      const a = addr as Record<string, unknown>;
      return {
        id: typeof a.id === 'string' ? a.id : undefined,
        fullAddress: typeof a.fullAddress === 'string' ? a.fullAddress : typeof a.full_address === 'string' ? a.full_address : undefined,
      };
    })(),
  };
}

/** Coerce unknown API values to string for deal list mapping (local to avoid relying on later helpers). */
function dealListString(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return undefined;
}

function dealListNumber(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

function optionalDealNumber(v: unknown): number | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

/**
 * Map a deal row from GET /api/deals or GET /api/brokers/:id/deals/ to shared Deal shape.
 * Tolerates camelCase or snake_case and missing fields so empty/partial responses never throw.
 */
export function mapDealFromApi(raw: unknown): Deal {
  if (raw == null || typeof raw !== 'object') {
    return {
      id: '',
      property: '',
      value: 0,
      commission: 0,
      brokers: 0,
      status: '',
    };
  }
  const r = raw as Record<string, unknown>;
  const id = r.id != null ? String(r.id) : '';
  const property =
    dealListString(r.property) ??
    dealListString(r.property_name) ??
    dealListString(r.propertyName) ??
    '';
  const value = dealListNumber(r.value ?? r.property_value ?? r.propertyValue);
  const commission = dealListNumber(r.commission);
  const brokers = dealListNumber(r.brokers);
  const status = dealListString(r.status) ?? '';
  const urlSlugRaw = r.urlSlug ?? r.url_slug;
  const urlSlug =
    urlSlugRaw == null || urlSlugRaw === ''
      ? null
      : typeof urlSlugRaw === 'string'
        ? urlSlugRaw.trim() || null
        : String(urlSlugRaw);
  const refRaw = r.referenceNumber ?? r.reference_number;
  const referenceNumber =
    refRaw == null || refRaw === ''
      ? null
      : dealListString(refRaw) ?? String(refRaw);
  const saeeGross = optionalDealNumber(r.saeeGross ?? r.saee_gross);
  const saeeVat = optionalDealNumber(r.saeeVat ?? r.saee_vat);
  const saeeNet = optionalDealNumber(r.saeeNet ?? r.saee_net);
  const mainBroker = dealListString(r.mainBroker ?? r.main_broker ?? r.leadBroker ?? r.lead_broker);
  return {
    id,
    urlSlug,
    referenceNumber,
    property,
    value,
    commission,
    brokers,
    status,
    saeeGross,
    saeeVat,
    saeeNet,
    mainBroker,
    createdAt:
      dealListString(r.createdAt) ??
      dealListString(r.created_at) ??
      undefined,
  };
}

/** Deal detail as returned by GET /api/deals/:id */
export interface DealDetail {
  id?: string;
  urlSlug?: string | null;
  referenceNumber?: string;
  creationDate?: string;
  transactionStatus?: string;
  customerName?: string;
  mobileNumber?: string;
  commStatus?: string;
  commissionType?: string;
  commissionRate?: number;
  clientRole?: string;
  totalCommission?: number;
  propertyName?: string;
  deedNo?: string;
  city?: string;
  district?: string;
  propertyValue?: number;
  description?: string;
  saeeGross?: number;
  saeeVat?: number;
  saeeNet?: number;
  associationShare?: number;
  saeeNetRevenue?: number;
  cancelReason?: string;
  refundRequestedAt?: string;
  refundReason?: string;
  refundDetails?: string;
  refundRejectedAt?: string;
  refundRejectReason?: string;
  refundRejectMessage?: string;
  clientName?: string;
  clientPhone?: string;
  /** Present only for the lead broker; used to build /pay/:dealId?token=... */
  paymentToken?: string;
  /** Present only for the lead broker; used to build a short payer link /p/:code */
  payerCode?: string;
  /** ANB virtual IBAN for payment; present when include_payment_token=True */
  virtualIban?: string;
  brokers?: Array<{
    id: string;
    brokerId?: string;
    name?: string;
    percent: number;
    amountDue: number;
    idNumber?: string;
    mobile?: string;
    status?: string;
    brokerRole?: 'Lead' | 'Sub';
    createdAt?: string;
    disputeReason?: string;
    disputeMessage?: string;
    disputedAt?: string;
  }>;
  /** Cumulative amount received via ANB credits. 0 when no credits yet. */
  paidAmount?: number;
  /** max(0, totalCommission − paidAmount). Non-zero when partially paid. */
  remainingAmount?: number;
  /** max(0, paidAmount − totalCommission). Non-zero when overpaid. */
  overpaidAmount?: number;
}

/** Request body for POST /api/deals – use shared schema type from @schemas/formSchemas */
export type { CreateDealBody, CreateDealBrokerEntry } from '@schemas/formSchemas';

/** Stats for overview (can be derived or from a dedicated endpoint) */
export interface AdminStats {
  totalBrokers: number;
  totalDeals: number;
  totalCommissionsDistributed: number;
}

export interface HealthResponse {
  ok: boolean;
  timestamp: string;
}

export interface SaudiGeoMetaRegion {
  id: string;
  name: string;
  name_en: string;
  name_ar: string;
}

export interface SaudiGeoMetaCity {
  id: string;
  regionId: string;
  name: string;
  name_en: string;
  name_ar: string;
}

export interface SaudiGeoMetaResponse {
  language: 'ar' | 'en';
  regions: SaudiGeoMetaRegion[];
  cities: SaudiGeoMetaCity[];
}

export interface SaudiGeoDistrictRow {
  id: string;
  name: string;
  name_en: string;
  name_ar: string;
}

export interface SaudiGeoDistrictsResponse {
  language: 'ar' | 'en';
  cityId: string;
  districts: SaudiGeoDistrictRow[];
}

export interface OverviewStats {
  grossRevenue: number;
  netRevenue: number;
  vatCollected: number;
  activeBrokers: number;
}

/** Auth verify-otp response data (backend envelope: { success, data }). Token omitted when using HttpOnly cookie. */
export interface AuthVerifyOtpData {
  token?: string;
  expiresAt: string;
  /** Broker: true when email, national ID, and date of birth are on file. Client/staff sessions are reported complete for routing. */
  profileCompleted: boolean;
  user: {
    id: string;
    mobile: string;
    name: string;
    nameAr?: string;
    email: string;
    role?: string;
    nationalId?: string;
    dateOfBirth?: string;
    profileCompleted?: boolean;
    emailVerified?: boolean;
    /** Broker FAL license verification (not Nafath identity). */
    falVerified?: boolean;
    /** Nafath identity verification completed — required for broker dashboard access. */
    nafathVerified?: boolean;
    /** Dynamic permissions for dashboard roles. */
    permissions?: string[];
  };
}

/** How the current session was established (payer link OTP vs broker/staff). */
export type AuthLoginContext = 'broker' | 'client';

/** Normalized from GET /api/auth/me (`role` on root or under `user`). */
export type AuthUserRole = 'system_administrator' | 'operations_manager' | 'financial_officer' | 'support_agent' | 'user' | string;

/** GET /api/auth/me – re-auth on load; returns user when session cookie is valid. */
export interface AuthMeData {
  user: AuthVerifyOtpData['user'];
  profileCompleted: boolean;
  loginContext: AuthLoginContext;
  /** Dashboard vs broker; mirrors backend User.role. */
  role?: AuthUserRole;
  /** Dynamic permissions for dashboard roles. */
  permissions?: string[];
}

export async function getAuthMe(): Promise<{ data: AuthMeData & { permissions: string[] } }> {
  const res = await apiClient.get<AuthMeData | { data?: AuthMeData }>('/api/auth/me', {
    /** Avoid Axios "error" path for unauthenticated bootstrapping (still 401 in Network panel). */
    validateStatus: (status) => status === 200 || status === 401 || status === 403,
  });
  if (res.status !== 200) {
    throw new Error('Not authenticated');
  }
  const raw = res.data;
  const data =
    raw && typeof raw === 'object' && 'user' in raw && raw.user
      ? (raw as AuthMeData)
      : (raw as { data?: AuthMeData })?.data;
  if (!data?.user) throw new Error('Not authenticated');
  const loginContext: AuthLoginContext = data.loginContext === 'client' ? 'client' : 'broker';
  const roleRaw = String(
    (data as { role?: string }).role ?? data.user?.role ?? 'user',
  )
    .trim()
    .toLowerCase();
  const permissions = data.user?.permissions ?? [];
  return { data: { ...data, loginContext, role: roleRaw, permissions } };
}

/** POST /api/auth/logout – clear session cookie and invalidate session. */
export async function authLogout(): Promise<void> {
  await apiClient.post('/api/auth/logout');
}

export type AuthOtpContext = 'broker' | 'client';

/** Backend error.code when payer mobile is not the broker-registered client phone. */
export const API_ERROR_CLIENT_PHONE_NOT_ON_DEAL = 'CLIENT_PHONE_NOT_ON_DEAL';

/** POST /api/auth/register – send OTP to phone (Saudi mobile). */
export async function authRegister(
  phone: string,
  opts?: { authContext?: AuthOtpContext; dealId?: string; token?: string },
): Promise<void> {
  try {
    const digits = phone.replace(/\D/g, '').replace(/^0?/, '').slice(-9);
    const body: { phone: string; authContext?: AuthOtpContext; dealId?: string; token?: string } = { phone: digits };
    if (opts?.authContext === 'client') {
      body.authContext = 'client';
      body.dealId = opts?.dealId;
      body.token = opts?.token;
    }
    const res = await apiClient.post<{ success: boolean; data?: { message: string; phone: string } }>(
      '/api/auth/register',
      body,
    );
    if (res.data?.data == null) throw new Error('Invalid response');
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/auth/verify-otp – exchange OTP for session token. */
export async function authVerifyOtp(
  phone: string,
  code: string,
  opts?: { authContext?: AuthOtpContext; dealId?: string; token?: string },
): Promise<AuthVerifyOtpData> {
  const digits = phone.replace(/\D/g, '').replace(/^0?/, '').slice(-9);
  const body: { phone: string; code: string; authContext?: AuthOtpContext; dealId?: string; token?: string } = {
    phone: digits,
    code: code.trim().slice(0, 6),
  };
  if (opts?.authContext === 'client') {
    body.authContext = 'client';
    body.dealId = opts?.dealId;
    body.token = opts?.token;
  }
  try {
    const res = await apiClient.post<AuthVerifyOtpData | { data?: AuthVerifyOtpData }>('/api/auth/verify-otp', body);
    const raw = res.data;
    const data =
      raw && typeof raw === 'object' && 'user' in raw && raw.user
        ? (raw as AuthVerifyOtpData)
        : (raw as { data?: AuthVerifyOtpData })?.data;
    if (!data?.user) throw new Error('Invalid or expired OTP');
    return {
      token: data.token ?? undefined,
      expiresAt: data.expiresAt ?? '',
      profileCompleted: Boolean(data.profileCompleted),
      user: data.user,
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/auth/complete – update nationalId, dateOfBirth, email (requires session). */
export async function authComplete(body: { nationalId: string; dateOfBirth: string; email: string }): Promise<{ data: { user: { id: string; email?: string; nationalId?: string } } }> {
  try {
    const res = await apiClient.post<{ success: boolean; data?: { user: { id: string; email?: string; nationalId?: string } } }>(
      '/api/auth/complete',
      body,
      { skipGlobalErrorToast: true },
    );
    const data = res.data?.data ?? (res.data as unknown as { user: { id: string; email?: string; nationalId?: string } });
    if (!data?.user) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Nafath address item returned by the TCC Nafath status endpoint. */
export interface NafathAddress {
  addressId: string;
  shortAddress?: string;
  fullAddress?: string;
  buildingNo?: string;
  streetName?: string;
  district?: string;
  city?: string;
  postalCode?: string;
}

export type NafathVerificationStatus = 'PENDING' | 'COMPLETED' | 'REJECTED' | 'EXPIRED';

/** POST /api/auth/nafath/initiate – start TCC Nafath flow; returns transId + random code to display. */
export async function authNafathInitiate(): Promise<{ transId: string; random: string }> {
  try {
    const res = await apiClient.post<{ transId?: string; random?: string; data?: { transId: string; random: string } }>(
      '/api/auth/nafath/initiate',
      {},
      { skipGlobalErrorToast: true },
    );
    const payload = res.data?.data ?? res.data;
    return { transId: payload?.transId ?? '', random: payload?.random ?? '' };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/auth/nafath/status?transId=... – poll until COMPLETED / REJECTED / EXPIRED. */
export async function authNafathStatus(transId: string): Promise<{
  status: NafathVerificationStatus;
  fullName?: string;
  fullNameAr?: string;
  name?: string;
  email?: string;
  nationalAddresses?: NafathAddress[];
}> {
  try {
    const res = await apiClient.get<{
      status?: NafathVerificationStatus;
      fullName?: string;
      fullNameAr?: string;
      name?: string;
      email?: string;
      nationalAddresses?: NafathAddress[];
      data?: { status: NafathVerificationStatus; fullName?: string; fullNameAr?: string; email?: string; nationalAddresses?: NafathAddress[] };
    }>(
      `/api/auth/nafath/status?transId=${encodeURIComponent(transId)}`,
      { skipGlobalErrorToast: true } as Parameters<typeof apiClient.get>[1],
    );
    const payload = res.data?.data ?? res.data;
    return {
      status: payload?.status ?? 'PENDING',
      fullName: payload?.fullName,
      fullNameAr: payload?.fullNameAr,
      name: (payload as { name?: string })?.name,
      email: payload?.email,
      nationalAddresses: payload?.nationalAddresses,
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/auth/national-address – save address (requires session). */
export async function authNationalAddress(body: { addressId: string; fullAddress?: string }): Promise<void> {
  try {
    const res = await apiClient.post<{ success: boolean; data?: { message: string } }>(
      '/api/auth/national-address',
      body,
      { skipGlobalErrorToast: true },
    );
    if (res.data?.data == null && (res.data as { success?: boolean })?.success !== true) throw new Error('Invalid response');
  } catch (e) {
    throw toApiError(e);
  }
}

/** Client profile shape (GET /api/profile). */
export interface ClientProfile {
  id: string;
  name: string;
  nameAr?: string;
  email: string;
  phone: string;
  nationalId: string;
  dateOfBirth: string;
  iban: string | null;
  falLicenseNumber: string | null;
  falVerified: boolean;
  emailVerified: boolean;
  nationalAddress: { id: string; fullAddress?: string } | null;
  /** Present when backend returns it (user has avatar). Omit for backward compatibility. */
  avatarUrl?: string | null;
}

/** GET /api/profile – current user profile (requires session). */
export async function getProfile(): Promise<{ data: ClientProfile }> {
  try {
    const res = await apiClient.get<{ success: boolean; data?: ClientProfile }>('/api/profile');
    const data = res.data?.data ?? (res.data as unknown as ClientProfile);
    if (!data?.id) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/profile/avatar – upload avatar (base64 data URL). Requires session. */
export async function uploadProfileAvatar(avatarDataUrl: string): Promise<{ data: { avatarUrl: string } }> {
  try {
    const res = await apiClient.post<{ success: boolean; data?: { avatarUrl: string } }>('/api/profile/avatar', { avatar: avatarDataUrl });
    const data = res.data?.data ?? (res.data as unknown as { avatarUrl: string });
    if (!data?.avatarUrl) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/profile – update email and/or national address (requires session). */
export async function patchProfile(body: {
  name?: string;
  email?: string;
}): Promise<{
  data: {
    user: { id: string; name: string; email: string };
    nationalAddress?: { id: string; fullAddress?: string; addressIdentifier?: string } | null;
  };
}> {
  try {
    const res = await apiClient.patch<{ success: boolean; data?: { user: { id: string; name: string; email: string } } }>('/api/profile', body);
    const data = res.data?.data ?? (res.data as unknown as { user: { id: string; name: string; email: string } });
    if (!data?.user) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/profile/fal – set FAL license number (requires session). */
export async function setProfileFalLicense(falLicenseNumber: string): Promise<{ data: { falLicenseNumber: string; falVerified: boolean } }> {
  const parsed = profileFalBodySchema.parse({ falLicenseNumber: (falLicenseNumber ?? '').trim() });
  try {
    const res = await apiClient.post<{ success?: boolean; data?: { falLicenseNumber: string; falVerified: boolean } }>('/api/profile/fal', parsed);
    const data = (res.data as { data?: { falLicenseNumber: string; falVerified: boolean } })?.data ?? (res.data as { falLicenseNumber: string; falVerified: boolean });
    if (!data?.falLicenseNumber) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/wallet – held/available/iban (requires session). */
export async function getWallet(): Promise<{ data: { heldAmount: number; availableAmount: number; iban: string | null } }> {
  try {
    const res = await apiClient.get<{ success: boolean; data?: { heldAmount: number; availableAmount: number; iban: string | null } }>('/api/wallet');
    const data = res.data?.data ?? (res.data as unknown as { heldAmount: number; availableAmount: number; iban: string | null });
    if (data == null) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Single wallet transaction (GET /api/wallet/transactions). */
export interface WalletTransactionItem {
  id: string;
  name: string;
  type: string;
  amount: number;
  date: string;
}

/** GET /api/wallet/transactions – paginated (requires session). */
export async function getWalletTransactions(params?: { page?: number; limit?: number }): Promise<{
  data: WalletTransactionItem[];
  meta?: { total: number; page: number; limit: number };
}> {
  try {
    const res = await apiClient.get<{ success: boolean; data?: WalletTransactionItem[]; meta?: { total: number; page: number; limit: number } }>('/api/wallet/transactions', { params });
    const json = res.data as unknown as {
      data?: unknown;
      meta?: { total: number; page: number; limit: number };
    };

    // Backend may return either:
    // - { success, data: WalletTransactionItem[], meta }
    // - { success, data: { success, data: WalletTransactionItem[], meta } } (double-wrapped)
    const payload =
      (json?.data && typeof json.data === 'object' && json.data !== null && 'data' in (json.data as Record<string, unknown>))
        ? (json.data as { data?: unknown; meta?: { total: number; page: number; limit: number } })
        : json;

    const data = Array.isArray(payload?.data) ? (payload.data as WalletTransactionItem[]) : [];
    const meta = payload?.meta ?? json?.meta;
    return { data, meta };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/wallet/iban – set or update IBAN (requires session). */
export async function updateWalletIban(iban: string): Promise<{ data: { message: string; iban: string } }> {
  const parsed = ibanBodySchema.parse({ iban: iban.trim().toUpperCase() });
  try {
    const res = await apiClient.post<{ success?: boolean; message?: string; iban?: string }>('/api/wallet/iban', parsed);
    const data = res.data as { message?: string; iban?: string };
    return { data: { message: data?.message ?? 'IBAN updated', iban: data?.iban ?? parsed.iban } };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/profile/settings-reset – clear settings-only data (IBAN, FAL) for current user. */
export async function resetSettings(): Promise<void> {
  try {
    await apiClient.post<{ success?: boolean; message?: string }>('/api/profile/settings-reset');
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Normalize Axios (or unknown) errors into a stable ApiError shape.
 * Used so callers can rely on message/status/code without depending on Axios types.
 */
function toApiError(e: unknown): ApiError {
  const err = e as AxiosError<{
    message?: string;
    detail?: string;
    error?: { code?: string; message?: string; details?: Record<string, string[] | string> } | string;
    code?: string;
  }>;
  const data = err.response?.data;
  const errObj = typeof data?.error === 'object' && data?.error !== null ? data.error : null;
  const message =
    errObj?.message ??
    data?.message ??
    (typeof data?.error === 'string' ? data.error : null) ??
    (typeof data?.detail === 'string' ? data.detail : null) ??
    (err.message || 'Request failed');
  const code = errObj?.code ?? data?.code;
  const details = errObj?.details;
  return {
    message: String(message),
    status: err.response?.status,
    code,
    details,
  };
}


export async function getHealth(): Promise<{ data: HealthResponse }> {
  try {
    const res = await apiClient.get<HealthResponse>('/api/health');
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/geo/saudi/ – regions + cities (districts via getSaudiGeoDistricts). */
export async function getSaudiGeoMeta(lang?: 'ar' | 'en'): Promise<{ data: SaudiGeoMetaResponse }> {
  try {
    const res = await apiClient.get<SaudiGeoMetaResponse | { data?: SaudiGeoMetaResponse }>('/api/geo/saudi/', {
      params: lang ? { lang } : undefined,
      // Short timeout: geo data is static and fast to serve. A 3 s cap prevents
      // a hung connection (e.g. no backend in E2E / offline environments) from
      // blocking the wizard UI for the full 15 s Axios default.
      timeout: 3000,
      skipGlobalErrorToast: true,
    });
    const data = unwrapSaeeDataPayload<SaudiGeoMetaResponse>(res);
    if (!data || !Array.isArray(data.cities)) throw new Error('Invalid response');
    return {
      data: {
        language: data.language === 'ar' ? 'ar' : 'en',
        regions: Array.isArray(data.regions)
          ? data.regions.map((r) => ({
              id: String((r as { id?: string; uuid?: string }).id ?? (r as { uuid?: string }).uuid ?? ''),
              name: String(r.name ?? ''),
              name_en: String((r as SaudiGeoMetaRegion).name_en ?? r.name ?? ''),
              name_ar: String((r as SaudiGeoMetaRegion).name_ar ?? r.name ?? ''),
            }))
          : [],
        cities: data.cities.map((city) => ({
          id: String((city as { id?: string; uuid?: string }).id ?? (city as { uuid?: string }).uuid ?? ''),
          regionId: String((city as SaudiGeoMetaCity).regionId ?? (city as { region_id?: string }).region_id ?? ''),
          name: String(city.name ?? ''),
          name_en: String((city as SaudiGeoMetaCity).name_en ?? city.name ?? ''),
          name_ar: String((city as SaudiGeoMetaCity).name_ar ?? city.name ?? ''),
        })),
      },
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/geo/saudi/districts/?city=<uuid> – districts for one city. */
export async function getSaudiGeoDistricts(
  cityId: string,
  lang?: 'ar' | 'en',
): Promise<{ data: SaudiGeoDistrictsResponse }> {
  try {
    const res = await apiClient.get<SaudiGeoDistrictsResponse | { data?: SaudiGeoDistrictsResponse }>(
      '/api/geo/saudi/districts/',
      {
        params: { city: cityId, ...(lang ? { lang } : {}) },
        timeout: 3000,
        skipGlobalErrorToast: true,
      },
    );
    const data = unwrapSaeeDataPayload<SaudiGeoDistrictsResponse>(res);
    if (!data || !Array.isArray(data.districts)) throw new Error('Invalid response');
    return {
      data: {
        language: data.language === 'ar' ? 'ar' : 'en',
        cityId: String(data.cityId ?? cityId),
        districts: data.districts.map((d) => ({
          id: String((d as { id?: string; uuid?: string }).id ?? (d as { uuid?: string }).uuid ?? ''),
          name: String(d.name ?? ''),
          name_en: String((d as SaudiGeoDistrictRow).name_en ?? d.name ?? ''),
          name_ar: String((d as SaudiGeoDistrictRow).name_ar ?? d.name ?? ''),
        })),
      },
    };
  } catch (e) {
    throw toApiError(e);
  }
}

export type GetBrokersOptions = {
  /**
   * When true, includes brokers missing display name or post-OTP profile (national ID + DOB).
   * Default false — backoffice list stays operationally clean.
   */
  includeIncomplete?: boolean;
};

/** GET /api/brokers – list brokers; normalizes backend fields (e.g. val_license) */
export async function getBrokers(options?: GetBrokersOptions): Promise<{ data: Broker[] }> {
  try {
    const res = await apiClient.get<BrokerRaw[] | { data?: BrokerRaw[]; brokers?: BrokerRaw[] }>(
      '/api/brokers',
      options?.includeIncomplete ? { params: { includeIncomplete: 'true' } } : undefined,
    );
    const json = res.data;
    const rawList = Array.isArray(json) ? json : (json?.data ?? json?.brokers ?? []);
    const data = (Array.isArray(rawList) ? rawList : []).map(mapBrokerFromApi);
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/brokers/:id – single broker (for detail page) */
export async function getBroker(id: string): Promise<{ data: Broker }> {
  try {
    const res = await apiClient.get<{ data?: BrokerRaw } | BrokerRaw>(`/api/brokers/${id}/`);
    const raw = (res.data as { data?: BrokerRaw })?.data ?? (res.data as BrokerRaw);
    if (!raw?.id && !(raw as BrokerRaw)?._id) throw new Error('Broker not found');
    return { data: mapBrokerFromApi(raw as BrokerRaw) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/brokers – create a new broker */
export async function createBroker(
  body: Partial<
    Pick<Broker, 'name' | 'mobile' | 'email' | 'falLicenseNumber' | 'iban' | 'nationalId' | 'dateOfBirth'>
  >,
): Promise<{ data: Broker }> {
  const parsed = createBrokerBodySchema.parse(body);
  try {
    const res = await apiClient.post<{ success: boolean; data?: BrokerRaw }>('/api/brokers', parsed);
    const raw = res.data?.data ?? (res.data as unknown as BrokerRaw);
    if (!raw) throw new Error('Invalid response');
    return { data: mapBrokerFromApi(raw) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/brokers/:id/deals/ – deals where this broker participates */
export async function getBrokerDeals(brokerId: string): Promise<{ data: Deal[] }> {
  try {
    const res = await apiClient.get<{ data?: Deal[] } | Deal[]>(`/api/brokers/${brokerId}/deals/`);
    const json = res.data;
    const rawList = Array.isArray(json) ? json : (json?.data ?? []);
    const data = (Array.isArray(rawList) ? rawList : []).map(mapDealFromApi);
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/brokers/:id/verify-fal – admin sets broker FAL as verified */
export async function verifyBrokerFal(brokerId: string): Promise<{ data: { id: string; falLicenseNumber: string | null; falVerified: boolean } }> {
  try {
    const res = await apiClient.post<{ success: boolean; data?: { id: string; falLicenseNumber: string | null; falVerified: boolean } }>(
      `/api/brokers/${brokerId}/verify-fal/`,
    );
    const data = res.data?.data ?? (res.data as unknown as { id: string; falLicenseNumber: string | null; falVerified: boolean });
    if (!data) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Backoffice-only broker activity log entry */
export interface BrokerActivityLogEntry {
  id: string;
  category: string;
  eventType: string;
  payload: Record<string, unknown>;
  dealId: string | null;
  actor: { id: string; name: string } | null;
  createdAt: string | null;
}

/** GET /api/brokers/:id/activity-log/ — paginated immutable audit trail */
export async function getBrokerActivityLog(
  brokerId: string,
  params?: { page?: number; limit?: number },
): Promise<{
  data: BrokerActivityLogEntry[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}> {
  try {
    const res = await apiClient.get(`/api/brokers/${brokerId}/activity-log/`, { params });
    const body = res.data as {
      data?: unknown;
      meta?: { total: number; page: number; limit: number; totalPages: number };
    };
    const meta = body.meta;
    const rawList = unwrapSaeeDataPayload<BrokerActivityLogEntry[]>(res);
    return {
      data: Array.isArray(rawList) ? rawList : [],
      meta: {
        total: meta?.total ?? 0,
        page: meta?.page ?? 1,
        limit: meta?.limit ?? 20,
        totalPages: meta?.totalPages ?? 0,
      },
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/brokers/:id/internal-notes/ — staff notes (not on broker app) */
export async function getBrokerInternalNotes(brokerId: string): Promise<{ data: BrokerInternalNote[] }> {
  try {
    const res = await apiClient.get(`/api/brokers/${brokerId}/internal-notes/`);
    const rawList = unwrapSaeeDataPayload<unknown[]>(res);
    const list = Array.isArray(rawList) ? rawList : [];
    return { data: list.map(mapInternalNoteFromApi) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/brokers/:id/internal-notes/ */
export async function postBrokerInternalNote(
  brokerId: string,
  body: { body: string },
): Promise<{ data: BrokerInternalNote }> {
  const parsed = brokerInternalNoteBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/brokers/${brokerId}/internal-notes/`, parsed);
    const raw = unwrapSaeeDataPayload<unknown>(res);
    if (raw == null || typeof raw !== 'object') throw new Error('Invalid response');
    return { data: mapInternalNoteFromApi(raw) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/deals – list deals. Pass view: 'sub' when authenticated to get deals where current user is a broker. */
export async function getDeals(params?: { view?: 'sub'; status?: string }): Promise<{ data: Deal[]; meta?: { total?: number } }> {
  try {
    const res = await apiClient.get<{ data?: Deal[]; meta?: { total?: number } }>('/api/deals', {
      params,
      skipGlobalErrorToast: true,
    });
    const json = res.data;
    const data = Array.isArray(json?.data) ? json.data : [];
    return { data, meta: json?.meta as { total?: number } | undefined };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Safely coerce to string for display (avoids "Objects are not valid as a React child"). */
function safeString(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return undefined;
}

/** GET /api/deals/:id or /api/deals/by-slug/:slug — normalizes deal detail payload. */
async function fetchDealDetail(
  apiPathSuffix: string,
  options?: { skipGlobalErrorToast?: boolean },
): Promise<{ data: DealDetail }> {
  try {
    const res = await apiClient.get<{ success?: boolean; data?: DealDetail } | DealDetail>(
      `/api/deals/${apiPathSuffix}`,
      { skipGlobalErrorToast: options?.skipGlobalErrorToast ?? false },
    );
    const json = res.data;
    const raw = (json && typeof json === 'object' && 'data' in json && json.data != null
      ? (json as { data: DealDetail }).data
      : json) as DealDetail | null | undefined;
    const data: DealDetail = raw && typeof raw === 'object' ? { ...raw } : {};
    if (!Array.isArray(data.brokers)) data.brokers = [];
    data.brokers = data.brokers.map((b, i) => {
      const id = b?.id != null ? String(b.id) : (b?.brokerId != null ? String(b.brokerId) : `broker-${i}`);
      const bx = b as typeof b & { ratePercent?: number | string };
      const percentNum = typeof bx?.percent === 'number' ? bx.percent : (typeof bx?.ratePercent === 'number' ? bx.ratePercent : (typeof bx?.percent === 'string' ? Number(bx.percent) : (typeof bx?.ratePercent === 'string' ? Number(bx.ratePercent) : 0)));
      return {
        id,
        brokerId: b?.brokerId != null ? String(b.brokerId) : undefined,
        name: safeString(b?.name) ?? '—',
        percent: percentNum,
        amountDue: typeof b?.amountDue === 'number' ? b.amountDue : (typeof b?.amountDue === 'string' ? Number(b.amountDue) : 0),
        idNumber: safeString(b?.idNumber),
        mobile: safeString(b?.mobile),
        status: safeString(b?.status) ?? 'Pending',
        brokerRole: (b?.brokerRole === 'Lead' || b?.brokerRole === 'Sub' ? b.brokerRole : (i === 0 ? 'Lead' : 'Sub')) as 'Lead' | 'Sub',
        createdAt: safeString(b?.createdAt) ?? (typeof b?.createdAt === 'string' ? b.createdAt : undefined),
        disputeReason: safeString(b?.disputeReason),
        disputeMessage: safeString(b?.disputeMessage),
        disputedAt: safeString(b?.disputedAt),
      };
    });
    data.referenceNumber = safeString(data.referenceNumber) ?? data.referenceNumber;
    data.paymentToken = safeString((data as unknown as { paymentToken?: unknown }).paymentToken) ?? data.paymentToken;
    data.payerCode = safeString((data as unknown as { payerCode?: unknown }).payerCode) ?? data.payerCode;
    data.creationDate =
      safeString(data.creationDate) ??
      safeString((data as unknown as { createdAt?: unknown }).createdAt) ??
      data.creationDate;
    data.transactionStatus =
      safeString(data.transactionStatus) ??
      safeString((data as unknown as { status?: unknown }).status) ??
      data.transactionStatus;
    data.refundReason =
      safeString(data.refundReason) ??
      safeString((data as unknown as { refund_reason?: unknown }).refund_reason) ??
      data.refundReason;
    data.refundDetails =
      safeString(data.refundDetails) ??
      safeString((data as unknown as { refund_details?: unknown }).refund_details) ??
      data.refundDetails;
    data.refundRequestedAt =
      safeString(data.refundRequestedAt) ??
      safeString((data as unknown as { refund_requested_at?: unknown }).refund_requested_at) ??
      data.refundRequestedAt;
    data.customerName =
      safeString(data.customerName) ??
      safeString((data as unknown as { clientName?: unknown }).clientName) ??
      data.customerName;
    data.mobileNumber =
      safeString(data.mobileNumber) ??
      safeString((data as unknown as { clientPhone?: unknown }).clientPhone) ??
      data.mobileNumber;
    data.clientRole =
      safeString((data as unknown as { clientRole?: unknown }).clientRole) ??
      data.clientRole;
    data.commStatus = safeString(data.commStatus) ?? data.commStatus;
    data.propertyName = safeString(data.propertyName) ?? data.propertyName;
    data.deedNo =
      safeString(data.deedNo) ??
      safeString((data as unknown as { deedNumber?: unknown }).deedNumber) ??
      data.deedNo;
    data.city = safeString(data.city) ?? data.city;
    data.district = safeString(data.district) ?? data.district;
    data.description = safeString(data.description) ?? data.description;
    data.cancelReason = safeString(data.cancelReason) ?? data.cancelReason;
    data.refundReason = safeString(data.refundReason) ?? data.refundReason;
    data.refundDetails = safeString(data.refundDetails) ?? data.refundDetails;
    data.refundRejectReason = safeString(data.refundRejectReason) ?? data.refundRejectReason;
    data.refundRejectMessage = safeString(data.refundRejectMessage) ?? data.refundRejectMessage;
    if (typeof data.totalCommission !== 'number') {
      data.totalCommission = typeof data.totalCommission === 'string' ? Number(data.totalCommission) : undefined;
    }
    if (typeof data.propertyValue !== 'number') {
      data.propertyValue = typeof data.propertyValue === 'string' ? Number(data.propertyValue) : undefined;
    }
    if (typeof data.saeeGross !== 'number') {
      data.saeeGross = typeof data.saeeGross === 'string' ? Number(data.saeeGross) : undefined;
    }
    if (typeof data.saeeVat !== 'number') {
      data.saeeVat = typeof data.saeeVat === 'string' ? Number(data.saeeVat) : undefined;
    }
    if (typeof data.saeeNet !== 'number') {
      data.saeeNet = typeof data.saeeNet === 'string' ? Number(data.saeeNet) : undefined;
    }
    if (typeof data.associationShare !== 'number') {
      data.associationShare = typeof data.associationShare === 'string' ? Number(data.associationShare) : undefined;
    }
    if (typeof data.saeeNetRevenue !== 'number') {
      data.saeeNetRevenue = typeof data.saeeNetRevenue === 'string' ? Number(data.saeeNetRevenue) : undefined;
    }
    data.refundRequestedAt = safeString(data.refundRequestedAt) ?? data.refundRequestedAt;
    data.refundRejectedAt = safeString(data.refundRejectedAt) ?? data.refundRejectedAt;
    data.urlSlug = safeString(data.urlSlug) ?? data.urlSlug;
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/deals/by-slug/:slug – deal detail by Latin url slug (same shape as getDeal). */
export async function getDealBySlug(slug: string): Promise<{ data: DealDetail }> {
  const trimmed = String(slug ?? '').trim();
  if (!trimmed) throw toApiError(new Error('Slug required'));
  return fetchDealDetail(`by-slug/${encodeURIComponent(trimmed)}`, { skipGlobalErrorToast: true });
}

/** GET /api/deals/:id – deal detail. Normalizes response so brokers is always an array and all display fields are primitives. */
export async function getDeal(id: string): Promise<{ data: DealDetail }> {
  return fetchDealDetail(String(id).trim());
}

/** GET /api/deals/:id/activity – derived timeline events for this deal (brokers only). */
export async function getDealActivity(id: string): Promise<{ data: DealActivityEvent[] }> {
  try {
    const res = await apiClient.get<{ success?: boolean; data?: DealActivityEvent[] } | DealActivityEvent[]>(
      `/api/deals/${id}/activity`,
    );
    const json = res.data;
    // Backend responses may be:
    // - ActivityEvent[]
    // - { success, data: ActivityEvent[] }
    // - { data: { data: ActivityEvent[] } } (double envelope in some deployments)
    const level1 = (json && typeof json === 'object' && 'data' in json
      ? (json as { data?: unknown }).data
      : json) as unknown;
    const level2 =
      level1 && typeof level1 === 'object' && 'data' in (level1 as object)
        ? (level1 as { data?: unknown }).data
        : level1;
    const data = Array.isArray(level2) ? (level2 as DealActivityEvent[]) : [];
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/deals/:id/client-view?token=... – payer-facing deal summary (token-validated, no auth). */
export async function getDealClientView(dealId: string, token: string): Promise<{ data: DealClientViewResponse }> {
  try {
    const res = await apiClient.get<{ success?: boolean; data?: DealClientViewResponse } | DealClientViewResponse>(
      `/api/deals/${dealId}/client-view`,
      { params: { token } },
    );
    // Backend may return either:
    // - payload directly, or
    // - { success, data: payload }, or (in some deployments) nested envelopes.
    const level1 = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: unknown }).data
      : res.data) as unknown;
    const level2 = (level1 && typeof level1 === 'object' && 'data' in (level1 as object)
      ? (level1 as { data?: unknown }).data
      : level1) as DealClientViewResponse | undefined;

    if (!level2 || typeof level2 !== 'object') throw new Error('Invalid client-view response');
    return { data: level2 };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/deals/dev-payer-link – dev only; returns dealId + token for Dev Nav redirect. */
export async function getDevPayerLink(): Promise<{ data: { dealId: string; token: string } }> {
  try {
    const res = await apiClient.get<{ success?: boolean; data?: { dealId: string; token: string } }>('/api/deals/dev-payer-link');
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data ? (res.data as { data: { dealId: string; token: string } }).data : res.data) as { dealId: string; token: string } | undefined;
    if (!data?.dealId || !data?.token) throw new Error('Invalid dev payer link response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/webhooks/bank-callback – simulate deposit (Bank Simulator / dev). */
export async function simulateBankDeposit(virtualIban: string, amount: number): Promise<{ data: { message?: string; alreadyProcessed?: boolean; dealId?: string; referenceNumber?: string } }> {
  try {
    const res = await apiClient.post<{ success?: boolean; data?: { message?: string; alreadyProcessed?: boolean; dealId?: string; referenceNumber?: string } }>(
      '/api/webhooks/bank-callback',
      { virtualIban: virtualIban.trim(), amount },
    );
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data ? (res.data as { data: unknown }).data : res.data) as { message?: string; alreadyProcessed?: boolean; dealId?: string; referenceNumber?: string } | undefined;
    return { data: data ?? {} };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals – create a new deal */
export async function createDeal(body: CreateDealBody): Promise<{ data: DealDetail }> {
  const parsed = createDealBodySchema.parse(body);
  try {
    const res = await apiClient.post<DealDetail | { data?: DealDetail }>('/api/brokerages', parsed);
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: DealDetail }).data
      : res.data) as DealDetail;
    return { data: data ?? ({} as DealDetail) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/deals/:id/calculate-splits */
export interface DealSplitBroker {
  brokerId: string;
  name: string;
  ratePercent: number;
  amountDue: number;
}
export interface DealSplits {
  totalCommission: number;
  brokers: DealSplitBroker[];
}
export async function getDealCalculateSplits(dealId: string): Promise<{ data: DealSplits }> {
  try {
    const res = await apiClient.get<DealSplits>(`/api/deals/${dealId}/calculate-splits`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/brokers – add broker to deal */
export async function addDealBroker(
  dealId: string,
  body: { brokerId?: string; phone?: string; ratePercent: number },
): Promise<{ data: { id: string; brokerId: string; name: string; ratePercent: number; amountDue: number } }> {
  const parsed = addDealBrokerBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/brokers/`, parsed);
    return { data: res.data as { id: string; brokerId: string; name: string; ratePercent: number; amountDue: number } };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/deals/:id/brokers/:brokerId – update broker rate */
export async function updateDealBroker(
  dealId: string,
  brokerId: string,
  body: { ratePercent: number },
): Promise<{ data: { id: string; ratePercent: number; amountDue: number } }> {
  const parsed = updateDealBrokerBodySchema.parse(body);
  try {
    const res = await apiClient.patch(`/api/deals/${dealId}/brokers/${brokerId}/`, parsed);
    return { data: res.data as { id: string; ratePercent: number; amountDue: number } };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/distribute-commission – distribute commission */
export async function distributeCommission(dealId: string): Promise<{ data: unknown }> {
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/distribute-commission/`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/release-escrow – release held escrow to available */
export async function releaseEscrow(dealId: string): Promise<{ data: unknown }> {
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/release-escrow/`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/notify – send notification (mock) */
export async function notifyDeal(dealId: string): Promise<{ data: unknown }> {
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/notify/`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/reset-sub-broker-approvals – set all sub-brokers to Pending so they must re-approve after lead edit */
export async function resetSubBrokerApprovals(dealId: string): Promise<{ data: { message: string } }> {
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/reset-sub-broker-approvals/`);
    return { data: res.data as { message: string } };
  } catch (e) {
    throw toApiError(e);
  }
}

/** DELETE /api/deals/:id – delete deal */
export async function deleteDeal(dealId: string): Promise<{ data: unknown }> {
  try {
    const res = await apiClient.delete(`/api/deals/${dealId}`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/request-refund – request refund (reason + optional details) */
export async function requestRefund(dealId: string, body: { reason: string; details?: string }): Promise<{ data: unknown }> {
  const parsed = requestRefundBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/request-refund`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Payer flow: step 1 — send OTP to deal client_phone (payment token auth). */
export async function payerRefundStart(
  dealId: string,
  paymentToken: string,
  body: { reason: string; details?: string },
): Promise<{ data: { otpDestination: string } }> {
  const parsed = requestRefundBodySchema.parse(body);
  try {
    const res = await apiClient.post<{ success?: boolean; data?: { otpDestination: string } }>(
      `/api/deals/${dealId}/payer-refund/start?token=${encodeURIComponent(paymentToken)}`,
      parsed,
    );
    const envelope = res.data as { success?: boolean; data?: { otpDestination: string } };
    const d = envelope?.data;
    if (!d?.otpDestination) throw new Error('Invalid response');
    return { data: d };
  } catch (e) {
    throw toApiError(e);
  }
}

/** Payer flow: step 2 — confirm OTP and submit refund request. */
export async function payerRefundConfirm(dealId: string, paymentToken: string, code: string): Promise<void> {
  const parsed = payerRefundConfirmBodySchema.parse({ code });
  try {
    await apiClient.post(
      `/api/deals/${dealId}/payer-refund/confirm?token=${encodeURIComponent(paymentToken)}`,
      parsed,
    );
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/refund/request-approve/ – send OTP to lead/admin mobile */
export async function requestRefundApproveOtp(dealId: string): Promise<{ data: { message: string; phone: string } }> {
  try {
    const res = await apiClient.post<{ success?: boolean; data?: { message: string; phone: string } }>(
      `/api/deals/${dealId}/refund/request-approve/`,
    );
    const data = res.data?.data ?? { message: 'Code sent', phone: '' };
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/refund/approve/ – confirm refund with OTP (after request-approve) */
export async function approveRefund(dealId: string, body: { code: string }): Promise<{ data: unknown }> {
  const parsed = refundApproveBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/refund/approve/`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/refund/reject/ – lead broker or admin; body: reason + optional message */
export async function rejectRefund(dealId: string, body: { reason: string; message?: string }): Promise<{ data: unknown }> {
  const parsed = rejectRefundBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/refund/reject/`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/deals/:id/cancel – cancel deal with reason */
export async function cancelDeal(dealId: string, body: { reason: string }): Promise<{ data: unknown }> {
  const parsed = cancelDealBodySchema.parse(body);
  try {
    const res = await apiClient.patch(`/api/deals/${dealId}/cancel`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/set-client-paid – mark client commission as paid */
export async function setClientPaid(dealId: string): Promise<{ data: unknown }> {
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/set-client-paid/`);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/brokers/:brokerId/request-approve – send OTP to broker's mobile */
export async function requestBrokerApprovalOtp(dealId: string, brokerId: string): Promise<{ data: { message: string; phone: string } }> {
  try {
    const res = await apiClient.post<{ data?: { message: string; phone: string } }>(
      `/api/deals/${dealId}/brokers/${brokerId}/request-approve/`,
    );
    const data = res.data?.data ?? { message: 'Code sent', phone: '' };
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/brokers/:brokerId/approve – broker approves split (with OTP code) */
export async function approveDealBroker(dealId: string, brokerId: string, body: { code: string }): Promise<{ data: unknown }> {
  const parsed = brokerApproveBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/brokers/${brokerId}/approve/`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/deals/:id/brokers/:brokerId/reject – broker objects with reason + message to lead broker */
export async function rejectDealBroker(dealId: string, brokerId: string, body: { reason: string; message?: string }): Promise<{ data: unknown }> {
  const parsed = brokerRejectBodySchema.parse(body);
  try {
    const res = await apiClient.post(`/api/deals/${dealId}/brokers/${brokerId}/reject/`, parsed);
    return { data: res.data };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getOverviewStats(): Promise<{ data: OverviewStats }> {
  const [{ data: brokers }, { data: deals }] = await Promise.all([
    getBrokers(),
    getDeals(),
  ]);

  const completedDeals = (deals ?? []).filter(
    (deal) => deal.status && deal.status.toLowerCase() === 'completed',
  );
  const grossRevenue = completedDeals.reduce(
    (sum, deal) => sum + (typeof deal.saeeGross === 'number' && Number.isFinite(deal.saeeGross) ? deal.saeeGross : 0),
    0,
  );
  const netRevenue = completedDeals.reduce(
    (sum, deal) => sum + (typeof deal.saeeNet === 'number' && Number.isFinite(deal.saeeNet) ? deal.saeeNet : 0),
    0,
  );
  const vatCollected = completedDeals.reduce(
    (sum, deal) => sum + (typeof deal.saeeVat === 'number' && Number.isFinite(deal.saeeVat) ? deal.saeeVat : 0),
    0,
  );
  const activeBrokers = (brokers ?? []).filter(
    (b) => b.status && b.status.toLowerCase() === 'active',
  ).length;

  return {
    data: {
      grossRevenue,
      netRevenue,
      vatCollected,
      activeBrokers,
    },
  };
}

// --- Admin (API_SPECIFICATIONS.md) ---

export interface CommissionFinancialDetails {
  /** Whether the deal is in Completed status (financial values are only meaningful when true) */
  isCompleted?: boolean;
  /** Gross Broker Commission (total_commission of the deal) */
  brokerGross: number;
  /** Saee Fee = GBC × 2.65% (inclusive of 15% VAT) */
  saeeFee: number;
  /** VAT portion (15%) = saeeFee - taxableBase */
  vatAmount: number;
  /** Taxable base = saeeFee / 1.15 */
  taxableBase: number;
  /** Association share = saeeFee × 30% */
  associationShare: number;
  /** Saee net revenue = saeeFee - associationShare */
  saeeNetRevenue: number;
  /** Whether a ZATCA Tax Invoice has been generated for this deal */
  hasInvoice: boolean;
}

export interface AdminCommissionReportEntry {
  id: string;
  dealReference: string;
  dealId?: string;
  city?: string;
  brokerName: string;
  /** Deal status from the Deal model (e.g. Completed, InProgress, Pending) */
  dealStatus?: string;
  brokerId: string;
  amount: number;
  status: string;
  createdAt: string;
  verifiedAt?: string | null;
  transferredAt?: string | null;
  financialDetails?: CommissionFinancialDetails;
}

export interface AdminCommissionReportSummary {
  totalCommissions: number;
  /** @deprecated Removed from API — completed-deal metrics only */
  activeDeals?: number;
  completedSalesValue: number;
  /** Sum of all VAT amounts — to be remitted to ZATCA */
  totalTaxPool: number;
  /** Total amount owed to the Real Estate Brokers Association */
  totalAssociationDues: number;
  /** Total Saee Fees minus Association Shares */
  netPlatformRevenue: number;
}

export interface AdminCommissionReportsParams {
  dateFrom?: string;
  dateTo?: string;
  brokerId?: string;
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}

/** Query string keys expected by Django (camelCase in UI → snake_case for API). */
function buildAdminCommissionReportsQuery(
  params?: AdminCommissionReportsParams,
): Record<string, string | number> | undefined {
  if (!params) return undefined;
  const q: Record<string, string | number> = {};
  if (params.page != null) q.page = params.page;
  if (params.limit != null) q.limit = params.limit;
  if (params.dateFrom) q.date_from = params.dateFrom;
  if (params.dateTo) q.date_to = params.dateTo;
  if (params.brokerId) q.broker_id = params.brokerId;
  if (params.search) q.search = params.search;
  if (params.status) q.status = params.status;
  return Object.keys(q).length ? q : undefined;
}

export interface AdminLogEntry {
  id: string;
  /** Legacy field kept for backward compat */
  level: string;
  /** Legacy field – new code uses description */
  message: string;
  actionType?: string | null;
  module?: string | null;
  description?: string | null;
  status?: 'Success' | 'Failure' | null;
  metadata?: Record<string, unknown> | null;
  userId?: string | null;
  user?: { id: string; name?: string; mobile?: string; email?: string } | null;
  createdAt: string;
}

export interface AdminNotificationItem {
  id: string;
  title: string;
  message: string;
  link: string;
  isRead: boolean;
  createdAt: string | null;
}

export interface AdminLogsParams {
  dateFrom?: string;
  dateTo?: string;
  date_from?: string;
  date_to?: string;
  action_type?: string;
  status?: string;
  module?: string;
  search?: string;
  level?: string;
  user?: string;
  page?: number;
  limit?: number;
}

/** GET /api/admin/notifications – latest 10 + unread count */
export async function getAdminNotifications(): Promise<{
  data: AdminNotificationItem[];
  meta: { unreadCount: number };
}> {
  try {
    const res = await apiClient.get<{
      data?: AdminNotificationItem[];
      meta?: { unreadCount?: number };
    }>('/api/admin/notifications');
    const data = unwrapSaeeDataPayload<AdminNotificationItem[]>(res) ?? [];
    const meta = res.data?.meta;
    return {
      data: Array.isArray(data) ? data : [],
      meta: { unreadCount: Number(meta?.unreadCount ?? 0) },
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/admin/notifications/:id/read */
export async function markAdminNotificationRead(id: string): Promise<void> {
  try {
    await apiClient.post(`/api/admin/notifications/${id}/read`);
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/admin/notifications/mark-all-read */
export async function markAllAdminNotificationsRead(): Promise<void> {
  try {
    await apiClient.post('/api/admin/notifications/mark-all-read');
  } catch (e) {
    throw toApiError(e);
  }
}

export interface AdminSettings {
  platformName?: string;
  defaultLanguage?: string;
  saeeCommissionRate?: number;
  [key: string]: unknown;
}

/** GET /api/admin/commission-reports – requires admin role */
export async function getAdminCommissionReports(
  params?: AdminCommissionReportsParams,
): Promise<{ data: AdminCommissionReportEntry[]; meta?: { total: number; page: number; limit: number } }> {
  try {
    const res = await apiClient.get('/api/admin/commission-reports', {
      params: buildAdminCommissionReportsQuery(params),
    });
    // SaeeJSONRenderer: { success, data: { data: [...] }, meta: { total, page, limit } }
    const top = res.data as { data?: { data?: AdminCommissionReportEntry[] }; meta?: { total: number; page: number; limit: number } } | undefined;
    const data = unwrapSaeeDataPayload<AdminCommissionReportEntry[]>(res) ?? [];
    const meta = top?.meta;
    return { data: Array.isArray(data) ? data : [], meta };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/admin/commission-reports/summary/ – aggregate widgets */
export async function getAdminCommissionReportsSummary(): Promise<{
  data: AdminCommissionReportSummary;
}> {
  try {
    const res = await apiClient.get('/api/admin/commission-reports/summary');
    // SaeeJSONRenderer: { success, data: { data: { totalCommissions, activeDeals, completedSalesValue } } }
    const summary = unwrapSaeeDataPayload<AdminCommissionReportSummary>(res);
    return {
      data: summary ?? {
        totalCommissions: 0,
        completedSalesValue: 0,
        totalTaxPool: 0,
        totalAssociationDues: 0,
        netPlatformRevenue: 0,
      },
    };
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * GET /api/admin/deals/<deal_id>/tax-invoice/pdf/
 * Downloads the stored (or first-generated) ZATCA tax invoice PDF for the deal.
 * Legacy path `/api/admin/commission-reports/invoice/<deal_id>/` remains supported server-side.
 */
export async function downloadCommissionInvoice(dealId: string): Promise<Blob> {
  try {
    const res = await apiClient.get(`/api/admin/deals/${dealId}/tax-invoice/pdf`, {
      responseType: 'blob',
      skipGlobalErrorToast: true,
    });
    return new Blob([res.data as BlobPart], { type: 'application/pdf' });
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/admin/logs – requires admin role */
export async function getAdminLogs(
  params?: AdminLogsParams,
): Promise<{ data: AdminLogEntry[]; meta?: { total: number; page: number; limit: number } }> {
  try {
    const res = await apiClient.get('/api/admin/logs', { params });
    // SaeeJSONRenderer: { success, data: { data: [...] }, meta: { total, page, limit } }
    const top = res.data as { data?: { data?: AdminLogEntry[] }; meta?: { total: number; page: number; limit: number } } | undefined;
    const data = unwrapSaeeDataPayload<AdminLogEntry[]>(res) ?? [];
    const meta = top?.meta;
    return { data: Array.isArray(data) ? data : [], meta };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/admin/settings – requires admin role */
export async function getAdminSettings(): Promise<{ data: AdminSettings }> {
  try {
    const res = await apiClient.get<AdminSettings | { data?: AdminSettings }>('/api/admin/settings');
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: AdminSettings }).data
      : res.data) as AdminSettings | undefined;
    return { data: data ?? {} };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/admin/settings – requires admin role */
export async function updateAdminSettings(
  body: Partial<Pick<AdminSettings, 'platformName' | 'defaultLanguage' | 'saeeCommissionRate'>>,
): Promise<{ data: AdminSettings }> {
  const parsed = updateAdminSettingsBodySchema.parse(body);
  try {
    const res = await apiClient.patch<AdminSettings | { data?: AdminSettings }>('/api/admin/settings', parsed);
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: AdminSettings }).data
      : res.data) as AdminSettings | undefined;
    return { data: data ?? {} };
  } catch (e) {
    throw toApiError(e);
  }
}

export interface PlatformResetResult {
  before: Record<string, number>;
  after: Record<string, number>;
  log: string[];
}

/** POST /api/admin/system/reset — system_administrator only */
export async function postAdminPlatformReset(body: PlatformResetBody): Promise<{ data: PlatformResetResult }> {
  const parsed = platformResetBodySchema.parse(body);
  try {
    const res = await apiClient.post<{ success?: boolean; data?: PlatformResetResult }>(
      '/api/admin/system/reset/',
      {
        confirm_phrase: parsed.confirm_phrase,
        keep_super_admins: parsed.keep_super_admins,
        ...(parsed.i_am_sure !== undefined ? { i_am_sure: parsed.i_am_sure } : {}),
      },
    );
    const payload = res.data as { success?: boolean; data?: PlatformResetResult } | undefined;
    if (!payload?.success || !payload.data) {
      throw new Error('Invalid reset response');
    }
    return { data: payload.data };
  } catch (e) {
    throw toApiError(e);
  }
}

// --- Admin Accounts (API_SPECIFICATIONS.md § Admin – Accounts) ---

export type AdminAccountRole = 'system_administrator' | 'operations_manager' | 'financial_officer' | 'support_agent';

export interface AdminAccount {
  id: string;
  name: string;
  email: string;
  mobile?: string | null;
  role: AdminAccountRole;
  parentId?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  subordinateCount?: number;
}

export interface AdminAccountDetail extends AdminAccount {
  parent?: { id: string; name: string; email: string } | null;
  subordinateIds?: string[];
}

export interface AdminAccountsParams {
  role?: AdminAccountRole;
  parentId?: string | null;
  page?: number;
  limit?: number;
  includeInactive?: boolean;
}

export interface CreateAdminAccountBody {
  name: string;
  email: string;
  mobile?: string;
  role: AdminAccountRole;
  parentId?: string | null;
}

/** GET /api/admin/accounts */
export async function getAdminAccounts(
  params?: AdminAccountsParams,
): Promise<{ data: AdminAccount[]; meta?: { total: number; page: number; limit: number } }> {
  try {
    const res = await apiClient.get<unknown>('/api/admin/accounts', { params });
    const json = res.data;

    // The backend renderer may return either:
    // - { success, data: AdminAccount[], meta? }
    // - { success, data: { data: AdminAccount[], meta? }, meta? } (double-wrapped when view already returns { data, meta })
    let data: AdminAccount[] = [];
    let meta: { total: number; page: number; limit: number } | undefined;

    if (Array.isArray(json)) {
      data = json;
    } else if (json && typeof json === 'object') {
      const obj = json as Record<string, unknown>;
      const payload = 'success' in obj ? obj.data : obj;
      if (Array.isArray(payload)) {
        data = payload as AdminAccount[];
      } else if (payload && typeof payload === 'object') {
        const payloadObj = payload as Record<string, unknown>;
        data = Array.isArray(payloadObj.data) ? (payloadObj.data as AdminAccount[]) : [];
        meta = payloadObj.meta as { total: number; page: number; limit: number } | undefined;
      }
      if (!meta && 'meta' in obj) {
        meta = obj.meta as { total: number; page: number; limit: number } | undefined;
      }
    }

    return { data, meta };
  } catch (e) {
    throw toApiError(e);
  }
}

/** GET /api/admin/accounts/:id */
export async function getAdminAccount(id: string): Promise<{ data: AdminAccountDetail }> {
  try {
    const res = await apiClient.get<AdminAccountDetail | { data?: AdminAccountDetail }>(`/api/admin/accounts/${id}`);
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: AdminAccountDetail }).data
      : res.data) as AdminAccountDetail | undefined;
    return { data: data ?? ({} as AdminAccountDetail) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/admin/accounts/:id */
export async function updateAdminAccount(
  id: string,
  body: { role?: AdminAccountRole; parentId?: string | null },
): Promise<{ data: AdminAccountDetail }> {
  const parsed = updateAdminAccountBodySchema.parse(body);
  try {
    const res = await apiClient.patch<AdminAccountDetail | { data?: AdminAccountDetail }>(
      `/api/admin/accounts/${id}`,
      parsed,
    );
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: AdminAccountDetail }).data
      : res.data) as AdminAccountDetail | undefined;
    return { data: data ?? ({} as AdminAccountDetail) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** POST /api/admin/accounts */
export async function createAdminAccount(body: CreateAdminAccountBody): Promise<{ data: AdminAccount }> {
  const parsed = createAdminAccountBodySchema.parse(body);
  try {
    const res = await apiClient.post<AdminAccount | { data?: AdminAccount }>('/api/admin/accounts', parsed);
    const data = (res.data && typeof res.data === 'object' && 'data' in res.data
      ? (res.data as { data?: AdminAccount }).data
      : res.data) as AdminAccount | undefined;
    return { data: data ?? ({} as AdminAccount) };
  } catch (e) {
    throw toApiError(e);
  }
}

/** PATCH /api/admin/accounts/:id/deactivate */
export async function deactivateAdminAccount(id: string): Promise<{ data: { id: string; isActive: false } }> {
  try {
    const res = await apiClient.patch<{ id: string; isActive: false }>(`/api/admin/accounts/${id}/deactivate`);
    return { data: res.data ?? { id, isActive: false } };
  } catch (e) {
    throw toApiError(e);
  }
}

export type AdminRBACRolePermissions = Record<string, string[]>;

export interface AdminRBACRolesResponse {
  roles: AdminRBACRolePermissions;
  allPermissions: string[];
}

/** GET /api/admin/rbac/roles – list all roles and their permissions (super admin only). */
export async function getAdminRBACRoles(): Promise<{ data: AdminRBACRolesResponse }> {
  const res = await apiClient.get<AdminRBACRolesResponse | { data?: AdminRBACRolesResponse }>('/api/admin/rbac/roles');
  const payload = unwrapSaeeDataPayload<AdminRBACRolesResponse>(res);
  return { data: payload ?? { roles: {}, allPermissions: [] } };
}

/** PATCH /api/admin/rbac/roles – bulk update roles' permissions (super admin only). */
export async function updateAdminRBACRolesBulk(
  updates: Record<string, string[]>,
): Promise<{ data: { saved: boolean; rolesUpdated: number; roles: AdminRBACRolePermissions } }> {
  const res = await apiClient.patch<
    { saved: boolean; rolesUpdated: number; roles: AdminRBACRolePermissions } | { data?: { saved: boolean; rolesUpdated: number; roles: AdminRBACRolePermissions } }
  >('/api/admin/rbac/roles', { updates });
  const payload = unwrapSaeeDataPayload<{ saved: boolean; rolesUpdated: number; roles: AdminRBACRolePermissions }>(res);
  return { data: payload ?? { saved: false, rolesUpdated: 0, roles: {} } };
}

/** GET /api/admin/rbac/me/permissions – current user's permissions. */
export async function getAdminRBACMyPermissions(): Promise<{ data: { role: string; permissions: string[] } }> {
  const res = await apiClient.get<{ role: string; permissions: string[] } | { data?: { role: string; permissions: string[] } }>(
    '/api/admin/rbac/me/permissions',
  );
  const payload = unwrapSaeeDataPayload<{ role: string; permissions: string[] }>(res);
  return { data: payload ?? { role: '', permissions: [] } };
}

export interface AdminRBACPermissionLog {
  id: string;
  role: string;
  previousPermissions: string[];
  newPermissions: string[];
  createdAt: string;
  actorName: string;
}

/** GET /api/admin/rbac/permission-logs – permission change audit trail. */
export async function getAdminRBACPermissionLogs(limit = 50): Promise<{ data: AdminRBACPermissionLog[] }> {
  const res = await apiClient.get<AdminRBACPermissionLog[] | { data?: AdminRBACPermissionLog[] }>(
    '/api/admin/rbac/permission-logs',
    { params: { limit } },
  );
  const payload = unwrapSaeeDataPayload<AdminRBACPermissionLog[]>(res);
  return { data: payload ?? [] };
}

// --- Staff Auth (email + password + optional TOTP) ---

export type StaffRole = 'system_administrator' | 'operations_manager' | 'financial_officer' | 'support_agent';

export interface StaffMeData {
  id: string;
  name: string;
  email: string;
  role: StaffRole;
  mfaEnabled: boolean;
  permissions: string[];
}

export interface StaffLoginResult {
  requiresTotp: boolean;
  pendingToken?: string;
  user?: StaffMeData;
}

export async function staffLogin(email: string, password: string): Promise<StaffLoginResult> {
  try {
    const res = await apiClient.post<{ success: boolean; data: StaffLoginResult }>(
      '/api/staff/auth/login',
      { email, password },
      { skipGlobalErrorToast: true },
    );
    return res.data.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function staffVerifyTotp(pendingToken: string, code: string): Promise<{ user: StaffMeData }> {
  try {
    const res = await apiClient.post<{ success: boolean; data: { user: StaffMeData } }>(
      '/api/staff/auth/verify-totp',
      { pendingToken, code },
      { skipGlobalErrorToast: true },
    );
    return res.data.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getStaffMe(): Promise<{ data: StaffMeData }> {
  const res = await apiClient.get<{ success: boolean; data: StaffMeData }>(
    '/api/staff/auth/me',
    { validateStatus: (status) => status === 200 || status === 401 || status === 403 },
  );
  if (res.status !== 200) throw new Error('Not authenticated');
  return { data: res.data.data };
}

export async function staffLogout(): Promise<void> {
  await apiClient.post('/api/staff/auth/logout');
}

export async function staffEnrollTotp(): Promise<{ secret: string; otpauthUrl: string; qrPngB64: string }> {
  try {
    const res = await apiClient.post<{ success: boolean; data: { secret: string; otpauthUrl: string; qrPngB64: string } }>(
      '/api/staff/auth/totp/enroll',
    );
    return res.data.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function staffConfirmTotp(code: string): Promise<void> {
  try {
    await apiClient.post('/api/staff/auth/totp/confirm', { code }, { skipGlobalErrorToast: true });
  } catch (e) {
    throw toApiError(e);
  }
}

export async function staffDisableTotp(password: string): Promise<void> {
  try {
    await apiClient.post('/api/staff/auth/totp/disable', { password }, { skipGlobalErrorToast: true });
  } catch (e) {
    throw toApiError(e);
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await apiClient.post('/api/staff/auth/password-reset/request', { email }, { skipGlobalErrorToast: true });
  } catch (e) {
    throw toApiError(e);
  }
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  try {
    await apiClient.post('/api/staff/auth/password-reset/confirm', { token, newPassword }, { skipGlobalErrorToast: true });
  } catch (e) {
    throw toApiError(e);
  }
}

// ─── Admin withdrawals ────────────────────────────────────────────────────────

export interface AdminWithdrawalItem {
  id: string;
  amount: string;
  currency: string;
  destinationIban: string;
  beneficiaryName: string;
  status: string;
  anbSequenceNumber: string;
  anbPaymentId: string;
  anbTransactionReference: string;
  anbStatus: string;
  anbChannel: string;
  feeAmount: string | null;
  rejectionReason: string;
  approvedAt: string | null;
  requestedAt: string;
  settledAt: string | null;
  failedAt: string | null;
  brokerMobile: string;
  brokerName: string;
}

export interface AdminWithdrawalsListParams {
  status?: string;
  broker?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export async function getAdminWithdrawals(params?: AdminWithdrawalsListParams): Promise<{
  data: AdminWithdrawalItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}> {
  try {
    const res = await apiClient.get('/api/admin/withdrawals', { params });
    const body = res.data as {
      data?: unknown;
      meta?: { page: number; limit: number; total: number; totalPages: number };
    };
    const inner = unwrapSaeeDataPayload<AdminWithdrawalItem[]>(res);
    const data = Array.isArray(inner) ? inner : [];
    const meta = body.meta ?? { page: 1, limit: 20, total: 0, totalPages: 0 };
    return { data, meta };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function approveWithdrawal(id: string): Promise<{ data: AdminWithdrawalItem }> {
  try {
    const res = await apiClient.post(`/api/admin/withdrawals/${id}/approve`);
    const data = unwrapSaeeDataPayload<AdminWithdrawalItem>(res);
    if (!data) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function rejectWithdrawal(id: string, reason: string): Promise<{ data: AdminWithdrawalItem }> {
  try {
    const res = await apiClient.post(`/api/admin/withdrawals/${id}/reject`, { reason });
    const data = unwrapSaeeDataPayload<AdminWithdrawalItem>(res);
    if (!data) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function cancelWithdrawal(id: string): Promise<{ data: AdminWithdrawalItem }> {
  try {
    const res = await apiClient.post(`/api/admin/withdrawals/${id}/cancel`);
    const data = unwrapSaeeDataPayload<AdminWithdrawalItem>(res);
    if (!data) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

export async function refreshWithdrawal(id: string): Promise<{ data: AdminWithdrawalItem }> {
  try {
    const res = await apiClient.post(`/api/admin/withdrawals/${id}/refresh`);
    const data = unwrapSaeeDataPayload<AdminWithdrawalItem>(res);
    if (!data) throw new Error('Invalid response');
    return { data };
  } catch (e) {
    throw toApiError(e);
  }
}

export const api = {
  getHealth,
  getSaudiGeoMeta,
  getSaudiGeoDistricts,
  getBrokers,
  getBroker,
  getBrokerDeals,
  createBroker,
  verifyBrokerFal,
  getDeals,
  getDeal,
  getDealBySlug,
  getDealClientView,
  getDevPayerLink,
  simulateBankDeposit,
  createDeal,
  getDealCalculateSplits,
  addDealBroker,
  updateDealBroker,
  distributeCommission,
  releaseEscrow,
  notifyDeal,
  resetSubBrokerApprovals,
  deleteDeal,
  getOverviewStats,
  getAdminCommissionReports,
  downloadCommissionInvoice,
  getAdminLogs,
  getAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  getAdminSettings,
  updateAdminSettings,
  getAdminAccounts,
  getAdminAccount,
  updateAdminAccount,
  createAdminAccount,
  deactivateAdminAccount,
  getAdminRBACRoles,
  updateAdminRBACRolesBulk,
  getAdminRBACMyPermissions,
  getAdminRBACPermissionLogs,
  getAdminWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  cancelWithdrawal,
  refreshWithdrawal,
  getAdminTransactions,
  exportAdminTransactionsUrl,
};

export default api;

// ─── Admin transactions ───────────────────────────────────────────────────────

export interface TransactionLinkedEntity {
  kind: 'deal' | 'withdrawal';
  id: string;
  label: string;
  url: string | null;
}

export interface AdminTransactionRow {
  id: string;
  txnDate: string | null;
  valueDate: string | null;
  partTrnType: 'CREDIT' | 'DEBIT' | string;
  amount: string;
  currency: string;
  sourceIban: string;
  destIban: string;
  outcome: string;
  refNum: string;
  uniqueId: string;
  linkedEntity: TransactionLinkedEntity | null;
}

export interface AdminTransactionsListParams {
  type?: 'credit' | 'debit' | '';
  outcome?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export async function getAdminTransactions(params?: AdminTransactionsListParams): Promise<{
  data: AdminTransactionRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}> {
  try {
    const res = await apiClient.get('/api/admin/transactions', { params });
    // SaeeJSONRenderer: { success, data: { data: [...] }, meta: { page, limit, total, totalPages } }
    const top = res.data as {
      data?: { data?: AdminTransactionRow[] };
      meta?: { page: number; limit: number; total: number; totalPages: number };
    } | undefined;
    const inner = unwrapSaeeDataPayload<AdminTransactionRow[]>(res) ?? [];
    const data = Array.isArray(inner) ? inner : [];
    const meta = top?.meta ?? { page: 1, limit: 25, total: 0, totalPages: 0 };
    return { data, meta };
  } catch (e) {
    throw toApiError(e);
  }
}

export function exportAdminTransactionsUrl(params?: AdminTransactionsListParams): string {
  const base = '/api/admin/transactions/export';
  if (!params) return base;
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.outcome) qs.set('outcome', params.outcome);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.search) qs.set('search', params.search);
  const str = qs.toString();
  return str ? `${base}?${str}` : base;
}
